import React, { useCallback, useRef } from "react";

/**
 * Pointer-tracked 3D tilt wrapper. Wraps any card content and applies a
 * subtle perspective rotation following the cursor. Disabled on touch devices.
 *
 * Props:
 *   - max:        max rotation in degrees (default 8)
 *   - className:  classes for the outer card
 *   - innerClassName: classes for the inner "lifted" layer
 *   - as:         element tag (default "article")
 */
export default function TiltCard({
  children,
  max = 8,
  className = "",
  innerClassName = "",
  as: Tag = "article",
  ...rest
}) {
  const ref = useRef(null);

  const handleMove = useCallback(
    (e) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const rotY = (x - 0.5) * 2 * max;
      const rotX = -(y - 0.5) * 2 * max;
      el.style.transform = `perspective(1000px) rotateX(${rotX.toFixed(
        2
      )}deg) rotateY(${rotY.toFixed(2)}deg)`;
      el.style.setProperty("--mx", `${(x * 100).toFixed(1)}%`);
      el.style.setProperty("--my", `${(y * 100).toFixed(1)}%`);
    },
    [max]
  );

  const handleLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "perspective(1000px) rotateX(0deg) rotateY(0deg)";
  }, []);

  return (
    <Tag
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`eu-tilt ${className}`}
      {...rest}
    >
      <div className={`eu-tilt-inner ${innerClassName}`}>{children}</div>
      <span className="eu-tilt-shine" />
    </Tag>
  );
}
